import type { Person, Position, RateCard } from './types.js';

// This person's own default rate for a role, matching a position's
// rateType — or undefined if they have no default set for that exact role
// (an exact string match against Person.defaultRates' keys, same convention
// as Position.role / RateCard.roles).
export function resolveDefaultRate(
  person: Person | undefined,
  role: string,
  rateType: 'day' | 'hourly' | undefined,
): number | undefined {
  const entry = person?.defaultRates?.[role];
  if (!entry) return undefined;
  return rateType === 'hourly' ? entry.hourlyRate?.amount : entry.dayRate?.amount;
}

// The rate to pre-fill when assigning someone to a position: the person's
// own default (People page) first, then the rate card's default pay rate
// for that role (distinct from the card's bill rate, which the quote uses),
// and finally the position's own rate — the original, only fallback before
// pay rates existed. All three stay just a starting point; still editable
// per assignment.
export function resolveAssignRate(
  person: Person | undefined,
  position: Position,
  rateCard: RateCard | undefined,
): number {
  const personRate = resolveDefaultRate(person, position.role, position.rateType);
  if (personRate != null) return personRate;

  const roleRate = rateCard && position.rateCardRole ? rateCard.roles[position.rateCardRole] : undefined;
  const payRate =
    position.rateType === 'hourly' ? roleRate?.payHourlyRate?.amount : roleRate?.payDayRate?.amount;
  if (payRate != null) return payRate;

  return position.rate.amount;
}
